import { useEffect, useState } from 'react';
import WebApp from '@twa-dev/sdk';

/**
 * Returns the current Telegram WebApp stable viewport height in pixels.
 *
 * Why JS-driven instead of `var(--tg-viewport-stable-height, 100dvh)`:
 * Telegram may set `--tg-viewport-stable-height` to `0px` when the WebApp is
 * minimized (e.g. Android hardware back). CSS fallback `var(..., 100dvh)` only
 * applies when the variable is *undefined* — not when it is `0px`. On reopen
 * the container collapses to 0 height until the next `viewportChanged` fires,
 * producing the visible "blank screen" bug.
 *
 * Tracks:
 * - `viewportChanged` — Telegram's authoritative height update
 * - `resize` — browser fallback when running outside Telegram
 *
 * Returns `window.innerHeight` when Telegram reports `0` so the UI never
 * collapses to an invisible 0-pixel container.
 */
export function useTelegramViewport(): number {
  const [height, setHeight] = useState<number>(() => {
    const vh = WebApp.viewportStableHeight;
    return vh > 0 ? vh : window.innerHeight;
  });

  useEffect(() => {
    const update = () => {
      const vh = WebApp.viewportStableHeight;
      setHeight(vh > 0 ? vh : window.innerHeight);
    };

    WebApp.onEvent('viewportChanged', update);
    window.addEventListener('resize', update);

    // Sync once on mount in case a viewportChanged event was missed
    // (e.g. hook mounts after Telegram already fired the event).
    update();

    return () => {
      WebApp.offEvent('viewportChanged', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return height;
}
